import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CheckIcon, FileArrowDownIcon } from '@/components/icons';
import { useI18n } from '@/i18n/I18nProvider';
import { useDownloadCharter } from '@/lib/hooks/useDownloadCharter';

interface CharterSignedViewProps {
  studentId: string;
  studentNumber: string | null;
  studentFullName: string;
  parentFullName: string;
  schoolName: string;
  schoolYearLabel: string;
}

export function CharterSignedView({
  studentId,
  studentNumber,
  studentFullName,
  parentFullName,
  schoolName,
  schoolYearLabel,
}: CharterSignedViewProps) {
  const { t, locale } = useI18n();
  const { downloadCharter, isDownloading } = useDownloadCharter();

  const handleDownload = React.useCallback(() => {
    downloadCharter({ studentNumber, academicYear: schoolYearLabel });
  }, [downloadCharter, studentNumber, schoolYearLabel]);

  return (
    <div className="space-y-6">
      {/* Already Signed Notice */}
      <Card className="border-2 border-green-500 shadow-lg">
        <CardContent className="pt-6">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white">
                  <CheckIcon size={24} />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-green-800 dark:text-green-300 mb-2">
                  {locale === 'ar' ? 'تم توقيع الميثاق بنجاح' : 'Charter Already Signed'}
                </h3>
                <p className="text-sm text-green-700 dark:text-green-400 mb-4">
                  {locale === 'ar'
                    ? 'لقد قمت بالفعل بتوقيع ميثاق الشراكة بين المدرسة وولي الأمر. يمكنك تحميل نسخة من الميثاق الموقع في أي وقت.'
                    : 'You have already signed the partnership charter between the school and parent. You can download a copy of the signed charter at any time.'}
                </p>

                {/* Student Info Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-green-200 dark:border-green-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        {locale === 'ar' ? 'الطالب: ' : 'Student: '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">{studentFullName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        {locale === 'ar' ? 'ولي الأمر: ' : 'Parent: '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">{parentFullName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        {locale === 'ar' ? 'المدرسة: ' : 'School: '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">{schoolName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        {locale === 'ar' ? 'السنة الدراسية: ' : 'Academic Year: '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">{schoolYearLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Download Button */}
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  <FileArrowDownIcon size={20} />
                  {locale === 'ar' ? 'تحميل الميثاق الموقع' : 'Download Signed Charter'}
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Back Button */}
      <div className="mt-6 text-center">
        <Link to={studentId ? `/students/${encodeURIComponent(studentId)}` : '/dashboard'}>
          <button className="aegov-btn btn-soft btn-secondary btn-sm" type="button">
            {t.parentConduct.navigation.close}
          </button>
        </Link>
      </div>
    </div>
  );
}
